import { SCHEME_MANIFESTS, careManifest } from "./manifests";
function parseColor(value) {
    if (!value)
        return null;
    const text = value.trim();
    const fn = text.match(/^rgba?\(([^)]+)\)$/i);
    if (fn) {
        const parts = fn[1]
            .split(/[,/\s]+/)
            .filter(Boolean)
            .map(Number);
        if (parts.length < 3 ||
            parts.slice(0, 3).some((n) => !Number.isFinite(n))) {
            return null;
        }
        const a = parts.length > 3 && Number.isFinite(parts[3]) ? parts[3] : 1;
        return {
            r: parts[0],
            g: parts[1],
            b: parts[2],
            a: Math.max(0, Math.min(1, a)),
        };
    }
    const hex = text.replace(/^#/, "");
    const expand = (h) => h.length <= 4
        ? h
            .split("")
            .map((c) => c + c)
            .join("")
        : h;
    const full = expand(hex);
    if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(full))
        return null;
    return {
        r: parseInt(full.slice(0, 2), 16),
        g: parseInt(full.slice(2, 4), 16),
        b: parseInt(full.slice(4, 6), 16),
        a: full.length === 8 ? parseInt(full.slice(6, 8), 16) / 255 : 1,
    };
}
function toHex({ r, g, b }) {
    const part = (n) => Math.max(0, Math.min(255, Math.round(n)))
        .toString(16)
        .padStart(2, "0");
    return `#${part(r)}${part(g)}${part(b)}`;
}
function mix(a, b, t) {
    return {
        r: a.r + (b.r - a.r) * t,
        g: a.g + (b.g - a.g) * t,
        b: a.b + (b.b - a.b) * t,
    };
}
function luminance({ r, g, b }) {
    const channel = (v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
function contrast(a, b) {
    const la = luminance(a);
    const lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
const WHITE = { r: 255, g: 255, b: 255 };
const BLACK = { r: 17, g: 17, b: 17 };
function flatten(value, over) {
    const parsed = parseColor(value);
    if (!parsed)
        return null;
    if (parsed.a >= 1)
        return toHex(parsed);
    if (!over)
        return null;
    return toHex(mix(over, parsed, parsed.a));
}
function readableOn(bg) {
    return contrast(bg, WHITE) >= contrast(bg, BLACK)
        ? toHex(WHITE)
        : toHex(BLACK);
}
const MIN_CONTRAST = 4.5;
function legible(candidate, grounds, fallback) {
    const rgb = parseColor(candidate);
    const inkRgb = parseColor(fallback);
    const backdrops = grounds.filter((g) => g !== null);
    if (!rgb || !inkRgb || !backdrops.length)
        return candidate;
    const passes = (c) => backdrops.every((bg) => contrast(c, bg) >= MIN_CONTRAST);
    if (passes(rgb))
        return candidate;
    for (let step = 1; step <= 10; step += 1) {
        const nudged = mix(rgb, inkRgb, step / 10);
        if (passes(nudged))
            return toHex(nudged);
    }
    return fallback;
}
function resolveTokens(binding) {
    const manifest = (binding && SCHEME_MANIFESTS[binding.key]) || careManifest;
    const defaults = manifest.defaultTokens;
    const overrides = binding === null || binding === void 0 ? void 0 : binding.tokens;
    return {
        key: manifest.key,
        tokens: {
            palette: { ...defaults.palette, ...overrides === null || overrides === void 0 ? void 0 : overrides.palette },
            fonts: { ...defaults.fonts, ...overrides === null || overrides === void 0 ? void 0 : overrides.fonts },
            extras: { ...defaults.extras, ...overrides === null || overrides === void 0 ? void 0 : overrides.extras },
        },
        chose(section, key) {
            const source = section === "palette"
                ? overrides === null || overrides === void 0 ? void 0 : overrides.palette
                : overrides === null || overrides === void 0 ? void 0 : overrides.extras;
            const value = source === null || source === void 0 ? void 0 : source[key];
            return typeof value === "string" && value.trim().length > 0;
        },
    };
}
const SURFACE_KEYS = ["surface", "card", "panel"];
const SURFACE_ALT_KEYS = ["surface2", "paper2", "paperDk", "chip", "glass"];
const HAIRLINE_KEYS = ["hair", "ruleSf", "hairSoft"];
const TINT_KEYS = ["gTint", "chip"];
function firstExtra(extras, keys) {
    if (!extras)
        return undefined;
    for (const key of keys) {
        const value = extras[key];
        if (typeof value === "string" && value.trim())
            return value;
    }
    return undefined;
}
export function familyPortalTheme(binding) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const { tokens, key, chose } = resolveTokens(binding);
    const { palette, fonts, extras } = tokens;
    const paperHex = (_a = flatten(palette.bg, WHITE)) !== null && _a !== void 0 ? _a : toHex(WHITE);
    const paperRgb = parseColor(paperHex);
    const inkHex = (_b = flatten(palette.ink, paperRgb)) !== null && _b !== void 0 ? _b : toHex(BLACK);
    const inkRgb = parseColor(inkHex);
    const accentHex = (_c = flatten(palette.accent, paperRgb)) !== null && _c !== void 0 ? _c : inkHex;
    const accentRgb = parseColor(accentHex);
    const light = paperRgb ? luminance(paperRgb) > 0.4 : true;
    const solid = (value) => { var _a; return (_a = flatten(value, paperRgb)) !== null && _a !== void 0 ? _a : undefined; };
    const stale = (section, companion, base) => chose("palette", base) && !chose(section, companion);
    const pick = (declared, isStale, derived) => { var _a; return (_a = (isStale ? (derived !== null && derived !== void 0 ? derived : declared) : (declared !== null && declared !== void 0 ? declared : derived))) !== null && _a !== void 0 ? _a : inkHex; };
    const sans = (_d = fonts.sans) !== null && _d !== void 0 ? _d : "system-ui, -apple-system, sans-serif";
    const surface = pick(solid(firstExtra(extras, SURFACE_KEYS)), stale("extras", "surface", "bg"), paperRgb ? toHex(mix(paperRgb, WHITE, light ? 0.65 : 0.07)) : undefined);
    const grounds = [paperRgb, parseColor(surface)];
    return {
        scheme: key,
        paper: paperHex,
        surface,
        surfaceAlt: pick(solid(firstExtra(extras, SURFACE_ALT_KEYS)), stale("extras", "surface2", "bg"), paperRgb ? toHex(mix(paperRgb, WHITE, light ? 0.35 : 0.04)) : undefined),
        ink: inkHex,
        inkSoft: legible(pick(solid(palette.inkDim), stale("palette", "inkDim", "ink") || stale("palette", "inkDim", "bg"), inkRgb && paperRgb ? toHex(mix(inkRgb, paperRgb, 0.35)) : undefined), grounds, inkHex),
        inkMuted: pick(solid(extras === null || extras === void 0 ? void 0 : extras.inkMuted), stale("extras", "inkMuted", "ink") || stale("extras", "inkMuted", "bg"), inkRgb && paperRgb ? toHex(mix(inkRgb, paperRgb, 0.5)) : undefined),
        hairline: pick((_e = solid(palette.rule)) !== null && _e !== void 0 ? _e : solid(firstExtra(extras, HAIRLINE_KEYS)), stale("palette", "rule", "bg"), inkRgb && paperRgb ? toHex(mix(paperRgb, inkRgb, 0.14)) : undefined),
        accent: accentHex,
        accentDeep: pick(solid(palette.accent2), stale("palette", "accent2", "accent"), accentRgb ? toHex(mix(accentRgb, BLACK, 0.28)) : undefined),
        accentTint: pick(solid(firstExtra(extras, TINT_KEYS)), stale("extras", "gTint", "accent") || stale("extras", "gTint", "bg"), accentRgb && paperRgb
            ? toHex(mix(paperRgb, accentRgb, light ? 0.16 : 0.24))
            : undefined),
        onAccent: (_f = (chose("extras", "onAccent") ? solid(extras === null || extras === void 0 ? void 0 : extras.onAccent) : undefined)) !== null && _f !== void 0 ? _f : (accentRgb ? readableOn(accentRgb) : toHex(WHITE)),
        sans,
        display: (_h = (_g = fonts.display) !== null && _g !== void 0 ? _g : fonts.serif) !== null && _h !== void 0 ? _h : sans,
        mono: (_j = fonts.mono) !== null && _j !== void 0 ? _j : "ui-monospace, SFMono-Regular, Menlo, monospace",
    };
}
